import React from 'react';
import { Formik } from 'formik';
import {
  Payment,
  CashRewardInfo,
  CashRewardType,
  RewardType,
  PhysicalRewardInfo,
  Site,
} from '@luckycart/xpn-types';
import parse from 'html-react-parser';

import { PrivacyInformations } from './PrivacyInformations';
import { ProvidedQueryParams } from '../../controllers/Reward.experience';
import { eventServiceInstance } from '../../services/shopper.event.service';
import { Button, StyledButtonSpan } from '../common-screen-display/common/common.style';
import { CashRewardForm } from './CashRewardForm';
import { PhysicalRewardForm } from './PhysicalRewardForm';

import LockIcon from './images/lock.svg';
import { RequiredFieldsNoticeText, ScreenReaderOnly, StyledForm } from './RewardForm.style';
import { StyledTitle } from '../common-screen-display/common/Common.screen.style';
import { removeBreaksAndEmptyParagraphs } from '../../utils/removeBreaksAndEmptyParagraphs';

export interface RewardFormValues {
  firstName: string;
  lastName: string;
  iban: string;
  bic: string;
  email: string;
  phone: string;
  address: string;
  additionalAddress: string;
  zipCode: string;
  city: string;
  recipients?: {
    firstName: string;
    lastName: string;
  }[];
}

interface Props {
  config: Payment;
  queryParams: ProvidedQueryParams;
  setIsFilled: (value: boolean) => void;
  rewardType: RewardType | null;
  siteConfig: Site;
}

export const RewardForm: React.FunctionComponent<Props> = ({
  config,
  queryParams: {
    siteKey,
    shopperId,
    experienceId,
    operationId,
    sequenceId,
    journeyId,
  },
  setIsFilled,
  rewardType,
  siteConfig,
}: Props) => {
  const [isBank, setIsBank] = React.useState<boolean>(true);
  const { forms, texts } = config;
  const isCashReward = rewardType === RewardType.CASH;
  const isPhysicalReward = rewardType === RewardType.PHYSICAL;

  const getRewardInfo = (values: RewardFormValues): CashRewardInfo | PhysicalRewardInfo | null => {
    const {
      firstName,
      lastName,
      iban,
      bic,
      email,
      phone,
      address,
      additionalAddress,
      zipCode,
      city,
      recipients,
    } = values;
    const formattedIban = iban.replace(/\s/gu, '');
    const formattedBic = bic.slice(0, 8);
    switch (rewardType) {
    case RewardType.CASH:
      return isBank
        ? {
          type: CashRewardType.BANK,
          firstname: firstName,
          lastname: lastName,
          bank: {
            iban: formattedIban,
            bic: formattedBic,
          },
        }
        : {
          type: CashRewardType.PAYPAL,
          firstname: firstName,
          lastname: lastName,
          paypal: {
            address: email,
          },
        };
    case RewardType.PHYSICAL: {
      const rewardInfo: PhysicalRewardInfo = {
        firstname: firstName,
        lastname: lastName,
        phone,
        address,
        additionalAddress,
        zipCode,
        city,
      };
      if (Array.isArray(recipients) && recipients.length > 0) {
        rewardInfo.recipients = recipients;
      }
      if (typeof email === 'string' && email.length > 0) {
        rewardInfo.email = values.email;
      }
      return rewardInfo;
    }
    default:
      return null;
    }
  };

  const initialValues: RewardFormValues = {
    firstName: '',
    lastName: '',
    iban: '',
    bic: '',
    email: '',
    phone: '',
    address: '',
    additionalAddress: '',
    zipCode: '',
    city: '',
    recipients: [],
  };

  return (
    <>
      <StyledTitle>{forms.title}</StyledTitle>
      <Formik
        initialValues={initialValues}
        validateOnChange={false}
        onSubmit={async (values) => {
          if (rewardType === null) {
            return;
          }
          const rewardInfo = getRewardInfo(values);
          if (rewardInfo !== null) {
            const isSent = await eventServiceInstance.sendRewardInfoFilledEvent({
              siteKey,
              shopperId,
              experienceId,
              rewardInfo,
              rewardType,
              operationId,
              sequenceId,
              journeyId,
            });
            setIsFilled(isSent);
          }
        }}
      >

        {() => <StyledForm>
          <RequiredFieldsNoticeText>
            {texts.notice.requiredFields}
          </RequiredFieldsNoticeText>
          {isCashReward &&
            <CashRewardForm
              config={config}
              isBank={isBank}
              setIsBank={setIsBank}
              siteConfig={siteConfig}
            />
          }
          {isPhysicalReward &&
                <PhysicalRewardForm
                  texts={texts}
                  missingMessage={texts.requiredMessage}
                  invalidMessage={texts.invalidMessage}
                />
          }

          <Button type="submit">
            <img src={LockIcon} alt="" />
            <StyledButtonSpan>
              {texts.confirm}
              <ScreenReaderOnly>
                {' '}{texts.submitButtonScreenReaderSuffix}
              </ScreenReaderOnly>
            </StyledButtonSpan>
          </Button>

          <PrivacyInformations text={parse(removeBreaksAndEmptyParagraphs(forms.desc ?? ''))} />
        </StyledForm>
        }
      </Formik>
    </>
  );
};
